/**
 * Learn section — chip filter + horizontal card track.
 *
 * Filtering: chip [data-blog] ↔ card .learn__item[data-blog] via .is-hidden.
 * View all href follows the active chip’s data-blog-url.
 * Empty: setup (no cards) vs filter (chip with zero matching articles).
 */
(() => {
  const TAG = 'learn-section';
  const DESKTOP_QUERY = '(min-width: 768px)';

  class LearnSection extends HTMLElement {
    connectedCallback() {
      if (this.dataset.learnReady === 'true') return;
      this.dataset.learnReady = 'true';

      this.mediaQuery = window.matchMedia(DESKTOP_QUERY);
      this.dotsHost = this.querySelector('[data-learn-dots]');
      this.emptyEl = this.querySelector('[data-learn-empty]');
      this.emptyTextEl = this.querySelector('[data-learn-empty-text]');

      this.#wrapTrack();

      this.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-learn-filter]');
        if (!chip || !this.contains(chip)) return;
        event.preventDefault();
        this.#setActiveBlog(chip.dataset.blog || '', chip.dataset.blogUrl || '');
      });

      this.addEventListener('keydown', (event) => {
        this.#handleChipKeydown(event);
      });

      this.addEventListener(
        'scroll',
        (event) => {
          if (event.target === this.querySelector('[data-learn-track]')) {
            this.#syncDots();
          }
        },
        true
      );

      this.mediaQuery.addEventListener('change', () => this.#syncDots());

      const chips = [...this.querySelectorAll('[data-learn-filter]')];
      const cards = this.#cardItems();

      // Prefer first chip that has matching cards; else first chip; else show all
      let initial =
        chips.find((chip) =>
          cards.some((card) => card.dataset.blog === chip.dataset.blog)
        ) || chips[0];

      // Only honour SSR preset when that category actually has cards (auto path).
      const preset = this.dataset.activeCategory || '';
      if (preset) {
        const presetChip = chips.find((chip) => chip.dataset.blog === preset);
        if (
          presetChip &&
          cards.some((card) => card.dataset.blog === preset)
        ) {
          initial = presetChip;
        }
      }

      if (initial) {
        this.#setActiveBlog(
          initial.dataset.blog || '',
          initial.dataset.blogUrl || ''
        );
      } else {
        this.#setActiveBlog('', '');
      }
    }

    // Suggested-reading cards come from an article metafield and may resolve no
    // blog handle, so membership cannot depend on [data-blog] being present.
    #cardItems() {
      return [...this.querySelectorAll('.learn__item')];
    }

    #handleChipKeydown(event) {
      const chip = event.target.closest('[data-learn-filter]');
      if (!chip || !this.contains(chip)) return;

      const keys = [
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ];
      if (!keys.includes(event.key)) return;

      const chips = [...this.querySelectorAll('[data-learn-filter]')];
      if (!chips.includes(chip)) return;

      event.preventDefault();

      const currentIndex = chips.indexOf(chip);
      const lastIndex = chips.length - 1;
      let nextIndex = currentIndex;

      if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = lastIndex;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
      } else {
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
      }

      const next = chips[nextIndex];
      next.focus();
      this.#setActiveBlog(next.dataset.blog || '', next.dataset.blogUrl || '');
    }

    #wrapTrack() {
      const surface = this.querySelector('.learn__surface');
      if (!surface) return;

      // Never pull Article filters into the track — chips also use [data-blog].
      const cards = [...surface.children].filter((el) => {
        if (el.matches('[data-learn-track], .learn__track')) return false;
        if (
          el.matches(
            '.article-filters, [data-learn-filters], .learn__filters, .learn__view-all, .title, :has(> .title)'
          )
        ) {
          return false;
        }
        if (
          el.querySelector('[data-learn-filter]') &&
          !el.querySelector('.learn__item, [data-learn-card]')
        ) {
          return false;
        }
        return (
          el.matches('.learn__item[data-blog]') ||
          Boolean(el.querySelector(':scope > .learn__item[data-blog]')) ||
          Boolean(el.querySelector(':scope > [data-learn-card]')) ||
          Boolean(el.querySelector(':scope > .blog-card'))
        );
      });

      let track = this.querySelector('[data-learn-track]');

      if (!cards.length) {
        if (track && !track.querySelector('.learn__item, .blog-card, [data-learn-card]')) {
          track.hidden = true;
        }
        return;
      }

      if (!track) {
        track = document.createElement('div');
        track.className = 'learn__track';
        track.setAttribute('data-learn-track', '');
        track.setAttribute('role', 'tabpanel');
        if (this.dataset.sectionId) {
          track.id = `LearnCards-${this.dataset.sectionId}`;
        }
        cards[0].before(track);
      }

      track.hidden = false;
      for (const card of cards) {
        track.appendChild(card);
      }
    }

    #setActiveBlog(blog, blogUrl) {
      if (blog) {
        this.dataset.activeCategory = blog;
      } else {
        delete this.dataset.activeCategory;
      }

      const chips = [...this.querySelectorAll('[data-learn-filter]')];
      let selectedIndex = -1;

      for (const [i, chip] of chips.entries()) {
        const selected = Boolean(blog) && chip.dataset.blog === blog;
        chip.classList.toggle('chip--selected', selected);
        chip.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) selectedIndex = i;
      }

      // Keep the tablist focusable when no chip matches (empty / “show all”).
      for (const [i, chip] of chips.entries()) {
        const focusable = selectedIndex >= 0 ? i === selectedIndex : i === 0;
        chip.tabIndex = focusable ? 0 : -1;
      }

      const items = this.#cardItems();
      let visible = 0;

      for (const item of items) {
        const itemBlog = item.dataset.blog || '';
        const match = !blog || !itemBlog || itemBlog === blog;
        const shell = item.closest('.shopify-block') || item;

        item.classList.toggle('is-hidden', !match);
        if (shell !== item) {
          shell.classList.toggle('is-hidden', !match);
        }

        const card = item.querySelector('[data-learn-card]') || item;
        if (
          card.classList?.contains('blog-card') ||
          card.matches?.('[data-learn-card]')
        ) {
          card.classList.toggle('blog-card--featured', match && visible === 0);
        }

        if (match) visible += 1;
      }

      if (blogUrl) {
        for (const link of this.querySelectorAll(
          '[data-learn-view-all], .learn__view-all a.button'
        )) {
          link.setAttribute('href', blogUrl);
        }
      }

      const track = this.querySelector('[data-learn-track]');
      if (track) track.scrollLeft = 0;

      this.#buildDots(visible);
      this.#syncDots();
      this.#syncEmpty(items.length, visible);
    }

    #syncEmpty(totalCards, visibleCards) {
      if (!this.emptyEl) return;

      const track = this.querySelector('[data-learn-track]');
      const setupMessage = this.emptyEl.dataset.emptySetup || '';
      const filterMessage = this.emptyEl.dataset.emptyFilter || '';

      if (totalCards === 0) {
        // Setup copy is rendered for the editor only. Without it there is
        // nothing a shopper can act on, so drop the section instead.
        if (!setupMessage) {
          const section = this.closest('.learn');
          if (section) section.hidden = true;
          return;
        }

        this.emptyEl.hidden = false;
        if (this.emptyTextEl) {
          this.emptyTextEl.textContent = setupMessage;
        }
        if (track) track.hidden = true;
        return;
      }

      if (visibleCards === 0) {
        this.emptyEl.hidden = false;
        if (this.emptyTextEl && filterMessage) {
          this.emptyTextEl.textContent = filterMessage;
        }
        if (track) track.hidden = true;
        return;
      }

      const section = this.closest('.learn');
      if (section) section.hidden = false;

      this.emptyEl.hidden = true;
      if (track) track.hidden = false;
    }

    #buildDots(count) {
      if (!this.dotsHost) return;
      this.dotsHost.replaceChildren();
      if (count <= 1) return;

      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('span');
        dot.className = i === 0 ? 'learn__dot learn__dot--active' : 'learn__dot';
        this.dotsHost.appendChild(dot);
      }
    }

    #syncDots() {
      if (!this.dotsHost) return;

      const track = this.querySelector('[data-learn-track]');
      const items = this.#cardItems().filter(
        (item) => !item.classList.contains('is-hidden')
      );
      const dots = [...this.dotsHost.children];

      if (this.mediaQuery.matches || items.length <= 1) {
        this.dotsHost.hidden = true;
        return;
      }

      this.dotsHost.hidden = false;

      let activeIndex = 0;
      if (!track || !items.length) {
        for (const [i, dot] of dots.entries()) {
          dot.classList.toggle('learn__dot--active', i === 0);
        }
        return;
      }

      const trackRect = track.getBoundingClientRect();
      let closest = Number.POSITIVE_INFINITY;

      for (const [i, item] of items.entries()) {
        const shell = item.closest('.shopify-block') || item;
        const shellRect = shell.getBoundingClientRect();
        const distance = Math.abs(shellRect.left - trackRect.left);
        if (distance < closest) {
          closest = distance;
          activeIndex = i;
        }
      }

      for (const [i, dot] of dots.entries()) {
        dot.classList.toggle('learn__dot--active', i === activeIndex);
      }
    }
  }

  if (!customElements.get(TAG)) {
    customElements.define(TAG, LearnSection);
  }
})();
